import CardData from '../dashboard/cardData';
import {
  ArrowRight,
  Briefcase,
  Calendar,
  CircleTick,
  Danger,
  Edit,
  NotificationBing,
  OutlineDanger,
  ShieldTick,
  TextBlock,
  User,
} from '../icons';

import { useTranslation } from 'react-i18next';

import { Button, CardCta, Divider, Paragraph } from '../Ant';
import { PrimaryCard } from '../../assets/styles/components/dashboard.styled';
import { Flex } from 'antd';
import { ITask } from '../../interface/tasks';
import { useLanguage } from '../../context/LanguageContext';
import { useTask } from '../../context/TaskContext';
import { ROUTES } from '../../constants/constants';
import {
  getLocalDateAndTime,
  getMatterIconClass,
  getMatterLabel,
} from '../../constants/utilityFunctions';
import {
  cardBorderColorMapping,
  GENERAL_ENTITY_DISABLED_ID,
  tagColorMapping,
} from '../../constants/tasks';
import { useState } from 'react';
import DescriptionModal from '../descriptionModal/descriptionModal';
import { useNavigationStack } from '../../context/NavigationStackContext';
import NoteIcons from '../common/NoteIcons';
import SkeletonEntityCard from '../common/skeleton/skeletonEntityCard';
import { getViewLabel } from '../../helpers/viewLabelHelper';

const CriticalCardDetail = ({
  task,
  completeOpenTask,
  isLoading = false,
}: {
  task?: ITask;
  completeOpenTask: (task?: ITask, operation?: string) => void;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();
  const { push } = useNavigationStack();
  const { getAppLanguageCode } = useLanguage();
  const { updateTask, clearInitialTask } = useTask();
  const [isShowMoreModalOpen, setIsShowMoreModalOpen] = useState<boolean>(false);
  const [showMoreContent, setShowMoreContent] = useState<
    { title: string; content: string } | undefined
  >(undefined);
  const translatedStatus =
    task?.status?.translations?.find((t) => t.language === getAppLanguageCode())?.translation ??
    '-';

  const handleEditTask = () => {
    if (task?.id) {
      updateTask(null);
      clearInitialTask();
      push(`${ROUTES.ADD_EDIT_CRITICAL_TASK}/${task?.id}`);
    }
  };
  const handleClose = () => {
    setIsShowMoreModalOpen(false);
  };
  const handleGoToMatter = () => {
    push(ROUTES.MATTER_DETAIL + `/${task?.matter?.id}`);
  };
  if (isLoading) {
    return (
      <SkeletonEntityCard
        rows={[1, 1, 2, 1, 2, 1, 1]}
        withSubcontentOnFirstRow
        className="py-10 px-10 h-full"
      />
    );
  }
  return (
    <>
      <PrimaryCard
        data-testid="critical-task-card"
        $isDetail
        className={`primary-card task-card d-flex flex-col gap-10 h-full ${cardBorderColorMapping[task?.statusId as string] || cardBorderColorMapping.default}`}
      >
        <div className="d-flex gap-10 w-full items-center">
          <div
            className={`status-chip d-flex justify-center items-center ${tagColorMapping[task?.statusId as string] || tagColorMapping.default}`}
          >
            {' '}
            {translatedStatus}
          </div>
          <NoteIcons hasImportantNotes={task?.hasImportantNotes} hasNotes={task?.hasNotes} />
        </div>
        <Divider />
        <div className="d-flex flex-col gap-10 flex-grow w-full h-full overflow-y-auto scroll-stable-gutter">
          <CardData
            type="subject"
            icon={<Danger className="text-clr-primary-error" />}
            title={t('task:criticalTask.criticalTask')}
            content={task?.subject ?? ''}
            contentclass="lineclamp-1"
            moreAction={
              <Button
                type="text"
                className="text-cta show-more"
                onClick={() => {
                  setShowMoreContent({
                    title: t('task:criticalTask.criticalTask'),
                    content: task?.subject ?? '',
                  });
                  setIsShowMoreModalOpen(true);
                }}
              >
                {t('common:more')}
              </Button>
            }
          />
          <CardData
            type="description"
            icon={<TextBlock />}
            title={t('task:description')}
            content={task?.description ?? '-'}
            contentclass="fw-400 lineclamp-2"
            moreAction={
              <Button
                data-testid="show-more-description-button"
                type="text"
                className="text-cta show-more"
                onClick={() => {
                  setShowMoreContent({
                    title: t('task:description'),
                    content: task?.description ?? '',
                  });
                  setIsShowMoreModalOpen(true);
                }}
              >
                {t('common:more')}
              </Button>
            }
          />
          <div className="d-flex justify-between">
            <CardData
              type="start-time"
              icon={<Calendar />}
              title={t('task:startTime')}
              content={getLocalDateAndTime(new Date(task?.startTime ?? ''))}
            />
            <CardData
              type="end-time"
              icon={<Calendar />}
              title={t('task:criticalTask.endTime')}
              content={getLocalDateAndTime(new Date(task?.dueTime ?? ''))}
            />
          </div>
          <CardData
            type="alert-time"
            icon={<NotificationBing />}
            title={t('task:alertTime')}
            content={getLocalDateAndTime(new Date(task?.alertTime ?? ''))}
          />
          <Divider />
          <div className="d-flex justify-between">
            <CardData
              type="matter"
              icon={
                <Briefcase className={getMatterIconClass(task?.matter?.invoiceableType ?? '')} />
              }
              title={getMatterLabel(t, task?.matter?.invoiceableType ?? '')}
              content={
                task?.matterId
                  ? (task?.matter?.principalCompany?.name ??
                    task?.matter?.principalPrivatePerson?.fullName ??
                    '-')
                  : '-'
              }
              subcontent={
                task?.matterId
                  ? (task?.matter?.matterNumber ?? '') + ' - ' + (task?.matter?.subject ?? '')
                  : ''
              }
              contentclass="lineclamp-1"
              subcontentclass="lineclamp-1"
              headingcontent={
                task?.matter && (
                  <Button
                    data-testid={`view-matter`}
                    type="text"
                    icon={<ArrowRight />}
                    iconPosition="end"
                    className="text-cta"
                    onClick={handleGoToMatter}
                  >
                    {getViewLabel(t, 'matter:matter')}
                  </Button>
                )
              }
            />
          </div>

          <CardData
            type="responsible-person"
            icon={<ShieldTick />}
            title={t('task:responsiblePerson')}
            content={task?.responsibleUser?.fullName ?? '-'}
          />
          <CardData
            type="other-responsible-persons"
            icon={<User />}
            title={t('task:otherResponsiblePersons')}
            content={
              task?.otherResponsibleUsers?.length
                ? task.otherResponsibleUsers.map((user) => user.user.fullName).join(', ')
                : '-'
            }
            contentclass="lineclamp-1"
          />
        </div>
        <Flex vertical gap={10} className="w-full">
          <Divider />
          <Flex gap={10}>
            {task?.statusId === GENERAL_ENTITY_DISABLED_ID ? (
              <CardCta
                data-testid="open-critical-task-button"
                onClick={() => {
                  completeOpenTask(task, 'open');
                }}
              >
                <OutlineDanger />
                <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="130%">
                  {t('task:openCriticalTask')}
                </Paragraph>
              </CardCta>
            ) : (
              <CardCta
                data-testid="mark-as-complete-button"
                onClick={() => {
                  completeOpenTask(task, 'complete');
                }}
              >
                <CircleTick />
                <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="130%">
                  {t('task:markAsComplete')}
                </Paragraph>
              </CardCta>
            )}

            {task?.statusId !== GENERAL_ENTITY_DISABLED_ID && (
              <CardCta onClick={handleEditTask} data-testid="edit-critical-task-button">
                <Edit />
                <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="130%">
                  {t('task:criticalTask.edit')}
                </Paragraph>
              </CardCta>
            )}
          </Flex>
        </Flex>
      </PrimaryCard>
      {/* Stays mounted through its closing animation, which is when antd hands focus back. */}
      <DescriptionModal
        title={showMoreContent?.title ?? ''}
        description={showMoreContent?.content ?? ''}
        open={isShowMoreModalOpen}
        onClose={handleClose}
      />
    </>
  );
};

export default CriticalCardDetail;
